import { Droppable } from "@hello-pangea/dnd";
import { useTranslate, type Identifier } from "ra-core";
import { useEffect, useMemo, useRef } from "react";

import { cn } from "@/lib/utils";

import type { Task, TaskStatus } from "../../types";
import { LoadMore } from "../LoadMore";
import { applyPendingMoves, type PendingMove } from "../pendingMoves";
import { sortByPosition } from "../positions";
import { TaskBoardCard } from "./TaskBoardCard";
import { useCardNames, useInfiniteTasks } from "./useTasksData";

const POSITION_SORT = { field: "position", order: "ASC" } as const;

export interface TaskColumnProps {
  status: TaskStatus;
  /** Server filter without the status. */
  filter: Record<string, unknown>;
  pendingMoves: Record<string, PendingMove<Task>>;
  /** Exposes the displayed tasks, needed to compute drop positions. */
  onItemsChange: (status: TaskStatus, tasks: Task[]) => void;
  salesNames: Map<Identifier, string>;
  onOpen: (task: Task) => void;
}

/** One status of the task board, loading 25 tasks at a time while scrolling. */
export const TaskColumn = ({
  status,
  filter,
  pendingMoves,
  onItemsChange,
  salesNames,
  onOpen,
}: TaskColumnProps) => {
  const translate = useTranslate();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const columnFilter = useMemo(() => ({ ...filter, status }), [filter, status]);
  const { tasks, total, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteTasks(columnFilter, POSITION_SORT);
  const cardNames = useCardNames(tasks);

  const { items, countDelta } = useMemo(() => {
    const placed = applyPendingMoves(
      tasks,
      pendingMoves,
      status,
      (task, position) => ({ ...task, status, position }),
    );
    return {
      items: sortByPosition(
        placed.items.map((task) => ({ ...task, position: task.position ?? 0 })),
      ),
      countDelta: placed.countDelta,
    };
  }, [tasks, pendingMoves, status]);

  useEffect(() => {
    onItemsChange(status, items);
  }, [onItemsChange, status, items]);

  const label = translate(`crm.operations.tasks.statuses.${status}`);

  return (
    <section
      className="flex w-72 shrink-0 flex-col rounded-lg bg-muted/40"
      aria-label={label}
    >
      <header className="flex items-baseline justify-between p-3 pb-1">
        <h3 className="text-sm font-semibold">{label}</h3>
        <span className="text-xs text-muted-foreground">
          {total + countDelta}
        </span>
      </header>
      <Droppable droppableId={status}>
        {(provided, snapshot) => (
          <div
            ref={(element) => {
              provided.innerRef(element);
              scrollRef.current = element;
            }}
            {...provided.droppableProps}
            className={cn(
              "flex max-h-[calc(100vh-15rem)] min-h-24 flex-1 flex-col gap-2 overflow-y-auto rounded-b-lg p-2",
              snapshot.isDraggingOver && "bg-muted",
            )}
            data-testid={`task-column-${status}`}
          >
            {items.map((task, index) => (
              <TaskBoardCard
                key={task.id}
                task={task}
                index={index}
                cardName={
                  task.contact_id != null
                    ? cardNames.get(task.contact_id)
                    : undefined
                }
                salesName={
                  task.sales_id != null
                    ? salesNames.get(task.sales_id)
                    : undefined
                }
                onOpen={onOpen}
              />
            ))}
            {provided.placeholder}
            <LoadMore
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={fetchNextPage}
              rootRef={scrollRef}
            />
          </div>
        )}
      </Droppable>
    </section>
  );
};
